import { useEffect, useRef, useState } from "react";
import { Button, QuietButton } from "../ui/button";
import { Screen } from "../ui/screen";
import { RULES_COUNTER_FROM, RULES_MAX_CHARS } from "../../lib/rules";

type Props = {
  initial: string;
  /** The saved document. Previewing is only meaningful against this, not the seed. */
  baseline: string;
  busy: boolean;
  error: string | null;
  errorLine: string | null;
  onPreview: (markdown: string) => void;
  onCancel: () => void;
};

/**
 * Manual editing needs no UI beyond a textarea (FR-2.1), so this is one — mono,
 * full-height, the document exactly as it is stored. Preview is the only way
 * out towards a save, because every path produces a reviewable diff (FR-2.5).
 */
export function RulesEditor({
  initial,
  baseline,
  busy,
  error,
  errorLine,
  onPreview,
  onCancel,
}: Props) {
  const [text, setText] = useState(initial);
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    field.current?.focus();
    field.current?.setSelectionRange(0, 0);
  }, []);

  const count = text.length;
  const over = count > RULES_MAX_CHARS;
  const unchanged = text.trim() === baseline.trim();

  return (
    <Screen wide>
      <p className="text-[13px] uppercase tracking-[0.18em] text-faint">Editing</p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight text-text sm:text-4xl">
        Your rules, as text.
      </h1>
      <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-muted">
        Markdown. Headings are the sections your doorman reads. Nothing is saved until you have seen
        the diff.
      </p>

      <div className="mt-8">
        <label htmlFor="nob-rules-editor" className="sr-only">
          Your House Rules
        </label>
        <textarea
          id="nob-rules-editor"
          ref={field}
          value={text}
          spellCheck={false}
          disabled={busy}
          onChange={(e) => setText(e.target.value)}
          className="h-[58vh] min-h-[22rem] w-full resize-y rounded-xl border border-border bg-surface/60 p-6
                     font-mono text-[13px] leading-6 text-text-soft placeholder:text-fainter
                     focus-visible:border-border-loud focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-focus/40 disabled:opacity-60"
        />
      </div>

      <div className="mt-4 min-h-[1.5rem]" aria-live="polite">
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        {errorLine && (
          <p className="mt-2 rounded-lg border border-border bg-surface/60 px-4 py-3 font-mono text-[13px] text-muted">
            {errorLine}
          </p>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button type="button" onClick={() => onPreview(text)} disabled={busy || over || unchanged}>
          Preview
        </Button>
        <QuietButton type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </QuietButton>
        {count > RULES_COUNTER_FROM && (
          <span
            className={`ml-auto font-mono text-xs tabular-nums ${over ? "text-accent-ink/80" : "text-faint"}`}
          >
            {count}/{RULES_MAX_CHARS}
          </span>
        )}
      </div>

      {over && (
        <p className="mt-3 text-xs text-faint">
          Your rules are as long as Nob will hold. Cut something before previewing.
        </p>
      )}
    </Screen>
  );
}
